import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Alert, Space, Typography } from 'antd';
import { QrcodeOutlined, EditOutlined, CameraOutlined, BulbOutlined, BulbFilled } from '@ant-design/icons';
import { Html5Qrcode } from 'html5-qrcode';

const { Text } = Typography;

/**
 * Componente de leitura de QR code pela câmera do navegador (WebRTC).
 * Compatível com tablet/iPad (Chrome/Edge Android e Safari iOS) em contexto HTTPS.
 *
 * Comportamento (autoStart = true, padrão):
 *  - Ao montar, tenta ligar a câmera automaticamente (menos atrito no chão de fábrica).
 *  - Se o auto-start falhar (iOS pode exigir gesto do usuário, permissão negada, sem câmera),
 *    cai no botão "Ativar câmera" — mesmo fluxo de antes, sem regressão.
 *  - Enquanto a câmera está ativa, mostra um botão discreto "Digitar código" para o operador
 *    informar o código manualmente sem parar o fluxo.
 *  - Após ler um QR, a câmera é parada e onScan(uid) é chamado. O debounce (lastScan) evita
 *    disparos repetidos do mesmo QR.
 *  - A câmera é liberada (stop/clear) ao trocar para o modo digitação e ao desmontar.
 *  - Lanterna (torch): quando o track de vídeo suporta, aparece um botão para
 *    ligar/desligar a lanterna — útil em ambientes com pouca luz.
 *
 * Props:
 *  - onScan(uid): chamado quando um QR é lido ou um código é informado manualmente.
 *  - onError(err): opcional, chamado em erro de câmera.
 *  - autoStart (bool, default true): se true, liga a câmera ao montar.
 *
 * Acessibilidade: aria-label nos botões e instruções em texto; feedback visual ao ler.
 */
const QrScanner = ({ onScan, onError, autoStart = true }) => {
  const containerId = 'qr-scanner-region';
  const scannerRef = useRef(null);
  const startingRef = useRef(false);
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualValue, setManualValue] = useState('');
  const [lastScan, setLastScan] = useState(null);
  // Lanterna (torch): só habilitada quando o track de vídeo suportar.
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);

  // Obtém o track de vídeo ativo a partir do elemento <video> injetado pelo
  // html5-qrcode no container. Usado para detectar suporte a torch e para
  // ligar/desligar a lanterna.
  const getVideoTrack = () => {
    const video = document.querySelector(`#${containerId} video`);
    if (!video || !video.srcObject) return null;
    const tracks = video.srcObject.getVideoTracks ? video.srcObject.getVideoTracks() : [];
    return tracks[0] || null;
  };

  // Detecta se o dispositivo/câmera suporta lanterna (torch) e atualiza o estado.
  const detectTorchSupport = () => {
    try {
      const track = getVideoTrack();
      const caps = track && track.getCapabilities ? track.getCapabilities() : null;
      setTorchSupported(!!(caps && caps.torch));
    } catch (e) {
      setTorchSupported(false);
    }
  };

  // Liga/desliga a lanterna aplicando a constraint "torch" no track ativo.
  const toggleTorch = async () => {
    if (!scannerRef.current) return;
    try {
      const next = !torchOn;
      await scannerRef.current.applyVideoConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (e) {
      // Se falhar (ex.: navegador/dispositivo sem suporte efetivo), desabilita o botão.
      setTorchSupported(false);
      setTorchOn(false);
    }
  };

  // Evita disparar o mesmo UID repetidamente (a câmera lê o mesmo QR várias vezes por segundo)
  const handleScan = (uid) => {
    if (lastScan === uid) return;
    setLastScan(uid);
    if (scannerRef.current) {
      try { scannerRef.current.stop(); } catch (e) { /* ignore */ }
    }
    setScanning(false);
    // Reseta a lanterna ao concluir a leitura (a câmera é liberada).
    setTorchOn(false);
    setTorchSupported(false);
    onScan && onScan(uid);
  };

  const startCamera = async () => {
    // Guard contra chamadas concorrentes (ex.: StrictMode monta o effect duas vezes
    // em dev), que duplicariam o elemento de vídeo no container.
    if (startingRef.current) return;
    startingRef.current = true;

    setCameraError(null);
    setManualMode(false);

    // Garante que não reste nenhuma instância anterior de câmera: para e limpa o
    // scanner previamente criado e esvazia o container antes de recriar.
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (e) { /* ignore */ }
      try { await scannerRef.current.clear(); } catch (e) { /* ignore */ }
      scannerRef.current = null;
    }
    const el = document.getElementById(containerId);
    if (el) el.innerHTML = '';

    // Expande a área de visualização antes de ligar a câmera (o html5-qrcode
    // precisa de um container com dimensões para injetar o vídeo).
    setScanning(true);
    try {
      const html5Qrcode = new Html5Qrcode(containerId);
      scannerRef.current = html5Qrcode;
      await html5Qrcode.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => handleScan(decodedText),
        () => { /* frame sem decode - ignora */ }
      );
      setScanning(true);
      // Após a câmera ativa, verifica se há suporte a lanterna (torch).
      detectTorchSupport();
    } catch (err) {
      // Auto-start pode falhar no iOS (exige gesto) ou se a permissão foi negada.
      // Reverte o estado e mostra o botão manual de ativação (sem regressão).
      setScanning(false);
      setCameraError('Não foi possível acessar a câmera. Toque em "Ativar câmera" ou digite o código.');
      onError && onError(err);
    } finally {
      startingRef.current = false;
    }
  };

  const stopCamera = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (e) { /* ignore */ }
      scannerRef.current = null;
    }
    setScanning(false);
    // Reseta a lanterna ao desligar a câmera (o track é liberado).
    setTorchOn(false);
    setTorchSupported(false);
  };

  // Auto-start ao montar (com fallback silencioso se falhar).
  useEffect(() => {
    if (autoStart) {
      // startCamera é async; dispara sem await para não bloquear a montagem.
      startCamera();
    }
    return () => {
      // cleanup ao desmontar — libera o hardware da câmera.
      if (scannerRef.current) {
        try { scannerRef.current.stop().then(() => scannerRef.current.clear()); } catch (e) { /* ignore */ }
      }
    };
    /* eslint-disable-next-line */
  }, []);

  const handleManualSubmit = () => {
    const value = manualValue.trim();
    if (!value) return;
    handleScan(value);
  };

  const goToManual = async () => {
    await stopCamera();
    setManualMode(true);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
      <div
        id={containerId}
        style={{ width: '100%', maxWidth: 360, minHeight: scanning ? 260 : 0 }}
        aria-label="Área de visualização da câmera para leitura de QR code"
      />

      {/* Câmera ativa: lanterna (se suportada), digitação e parar */}
      {scanning && (
        <Space direction="vertical" align="center" style={{ width: '100%' }} size="small">
          {torchSupported && (
            <Button
              type="default"
              icon={torchOn ? <BulbFilled /> : <BulbOutlined />}
              onClick={toggleTorch}
              aria-label={torchOn ? 'Desligar lanterna' : 'Ligar lanterna'}
              aria-pressed={torchOn}
            >
              {torchOn ? 'Lanterna acesa' : 'Ligar lanterna'}
            </Button>
          )}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={goToManual}
            aria-label="Digitar o código manualmente em vez de usar a câmera"
          >
            Digitar código
          </Button>
          <Button
            danger
            type="text"
            icon={<QrcodeOutlined />}
            onClick={stopCamera}
            aria-label="Parar câmera"
          >
            Parar câmera
          </Button>
        </Space>
      )}

      {/* Câmera parada (auto-start falhou ou não habilitado): ativar + digitar */}
      {!scanning && !manualMode && (
        <Space direction="vertical" align="center" style={{ width: '100%' }}>
          <Button
            type="primary"
            size="large"
            icon={<CameraOutlined />}
            onClick={startCamera}
            aria-label="Iniciar câmera para ler QR code"
            block
          >
            Ativar câmera para ler QR
          </Button>
          <Button
            size="large"
            icon={<EditOutlined />}
            onClick={goToManual}
            aria-label="Digitar o código do tambor manualmente"
            block
          >
            Digitar código manualmente
          </Button>
        </Space>
      )}

      {cameraError && (
        <Alert type="warning" message={cameraError} showIcon />
      )}

      {manualMode && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text>Informe o código ou UID do tambor:</Text>
          <Input
            size="large"
            placeholder="Ex.: PETROBRAS-260704-T01"
            value={manualValue}
            onChange={e => setManualValue(e.target.value)}
            onPressEnter={handleManualSubmit}
            aria-label="Código ou UID do tambor"
          />
          <Button type="primary" size="large" block onClick={handleManualSubmit}>
            Buscar tambor
          </Button>
          <Button
            size="large"
            block
            icon={<CameraOutlined />}
            onClick={() => { setManualMode(false); startCamera(); }}
          >
            Voltar para a câmera
          </Button>
        </Space>
      )}
    </div>
  );
};

export default QrScanner;
